import {ListType} from '@frontapp/editor-html';
import {isUndefined} from 'lodash';
import React, {FC} from 'react';
import styled from 'styled-components';

import {Trans} from '../../../../../libs/shared/core/src/helpers/internationalization/react';
import {CheckBox} from '../../../../../libs/shared/ui/src/components/booleanInputs/checkBox';
import {ToolbarButton} from '../../../../../libs/shared/ui/src/components/buttons/toolbarButton';
import {SimpleDropdown} from '../../../../../libs/shared/ui/src/components/dropdowns/simpleDropdown/simpleDropdown';
import {SimpleDropdownItem} from '../../../../../libs/shared/ui/src/components/dropdowns/simpleDropdown/simpleDropdownItem';
import {
  PopoverCoordinator,
  PopoverTransferProps,
} from '../../../../../libs/shared/ui/src/components/popovers/popoverCoordinator';
import {UserThemeConsumer} from '../../../../../libs/shared/ui/src/theme/userThemeConsumer';

/*
 * Props.
 */

export interface FormattingListProps {
  canIndent: (key?: string) => boolean;
  canOutdent: (key?: string) => boolean;
  onIndentButtonClick?: () => void;
  onOutdentButtonClick?: () => void;
  listType: ListType | undefined;
  onListTypeChange(listType: ListType | undefined): void;
  hasListAutoFormatting: boolean;
  toggleListAutoFormatting?: () => void;
}

const StyledToggleAutoFormatDiv = styled.div`
  font-size: 14px;
  margin: 2px 8px 2px 2px;
`;

/*
 * Component.
 */

export const FormattingList: FC<FormattingListProps> = (props) => (
  <PopoverCoordinator
    renderButton={(isOpen) => renderButton(isOpen, !isUndefined(props.listType))}
    renderModal={(modalProps) => renderDropdown(modalProps, props)}
  />
);

/*
 * Button.
 */

function renderButton(isOpen: boolean, isActive: boolean) {
  return (
    <UserThemeConsumer>
      {(theme) => (
        <ToolbarButton
          iconName="formattingList"
          isActive={isActive}
          hasHover={isOpen}
          shouldPreventMouseDown
          label={<Trans id="formatting-lists">Lists</Trans>}
          isOpen={isOpen}
          color={theme.greys.shade80}
          hoverColor={theme.greys.shade80}
        />
      )}
    </UserThemeConsumer>
  );
}

/*
 * Dropdown.
 */

function renderDropdown(modalProps: PopoverTransferProps, props: FormattingListProps) {
  return (
    <SimpleDropdown {...modalProps} footer={renderAutoFormatToggler(props)}>
      {renderFormattingListDropdownItems(props)}
    </SimpleDropdown>
  );
}

function renderAutoFormatToggler(props: FormattingListProps) {
  const {hasListAutoFormatting, toggleListAutoFormatting} = props;

  return (
    toggleListAutoFormatting && (
      <StyledToggleAutoFormatDiv>
        {/* TODO: Instead of toggling, use the value passed to the change handler. */}
        <CheckBox isChecked={hasListAutoFormatting} onChange={toggleListAutoFormatting}>
          <Trans id="toggle-auto-formatting">Auto-Formatting</Trans>
        </CheckBox>
      </StyledToggleAutoFormatDiv>
    )
  );
}

export function renderFormattingListDropdownItems(props: FormattingListProps) {
  const {canIndent, canOutdent, onIndentButtonClick, onOutdentButtonClick, listType, onListTypeChange} =
    props;

  const listTypeIsOrdered = listType === ListType.ORDERED;
  const listTypeIsUnordered = listType === ListType.UNORDERED;

  return (
    <>
      <SimpleDropdownItem
        isChecked={listTypeIsUnordered}
        onSelect={() => onListTypeChange(listTypeIsUnordered ? undefined : ListType.UNORDERED)}
      >
        <Trans id="formatting-list-bulleted">Bulleted List</Trans>
      </SimpleDropdownItem>
      <SimpleDropdownItem
        isChecked={listTypeIsOrdered}
        onSelect={() => onListTypeChange(listTypeIsOrdered ? undefined : ListType.ORDERED)}
      >
        <Trans id="formatting-list-numbered">Numbered List</Trans>
      </SimpleDropdownItem>
      {onIndentButtonClick && (
        <SimpleDropdownItem isDisabled={!canIndent} onSelect={onIndentButtonClick}>
          <Trans id="formatting-indent-right">Indent Right</Trans>
        </SimpleDropdownItem>
      )}
      {onOutdentButtonClick && (
        <SimpleDropdownItem isDisabled={!canOutdent} onSelect={onOutdentButtonClick}>
          <Trans id="formatting-indent-left">Indent Left</Trans>
        </SimpleDropdownItem>
      )}
    </>
  );
}
